import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { collection, doc } from 'firebase/firestore';

import { useNavigate, useSearchParams } from 'react-router';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GratiPageWrapper from '../components/common/GratiPageWrapper';
import Hourglass from '../components/common/Hourglass';
import CourseEditor from '../components/course/CourseEditor';
import { ActionProps, useApp } from '../contextProviders/AppProvider';
import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import useUsgaCourseDraft from '../dataHooks/useUsgaCourseDraft';
import { useCourseMutators } from '../dataMutators/useCourseMutators';
import useCourseUsgaMutators from '../dataMutators/useCourseUsgaMutators';
import useUsgaCourseSearchMutators from '../dataMutators/useUsgaCourseSearchMutators';
import { firestore } from '../firebase';
import Course from '../types/Course';
import DefaultObjects from '../types/DefaultObjects';
import { USGA_COURSE_SEARCH_STATUSES } from '../types/UsgaCourseSearch';
import { HANDICAP_LABELS } from '../utils/handicapUtils';
import { courseFromUsgaDraft, courseImportPath, usgaCourseIdsOf } from '../utils/usgaCourseUtils';

/**
 * Creates a course from scratch, or — with `?usga=<ids>` — imports each listed USGA course in turn,
 * seeding the editor from its draft (useUsgaCourseDraft) and moving to the next id after each save.
 */
export default function CourseCreatePage(): ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setPageName } = useApp();
  const { isGod } = useAuth();
  const { userState } = useData();
  const { addCourse } = useCourseMutators();
  const { createUsgaData } = useCourseUsgaMutators();
  const { requestUsgaCourseDrafts } = useUsgaCourseSearchMutators();

  const [pageActions, setPageActions] = useState<ActionProps[] | null>(null);
  const [godActions, setGodActions] = useState<ReactElement | null>(null);

  const usgaCourseIds = useMemo(() => usgaCourseIdsOf(searchParams), [searchParams]);
  const usgaCourseId = usgaCourseIds[0] ?? null;
  const isImport = usgaCourseId !== null;
  const { draft, exists: draftExists, isLoading: isDraftLoading } = useUsgaCourseDraft(usgaCourseId);

  useEffect(() => {
    setPageName(isImport ? 'Import Course' : 'Create Course');
  }, [isImport, setPageName]);

  // A deep link (or a reload) to an id nobody has requested yet: ask the server for its draft.
  useEffect(() => {
    if (usgaCourseId && draftExists === false) requestUsgaCourseDrafts([usgaCourseId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usgaCourseId, draftExists]);

  const creatorId = userState.userProfile?.itemId || '';
  const isDraftReady = draft?.status === USGA_COURSE_SEARCH_STATUSES.DONE;

  // Pre-generate the document ID so we can use it for the logo path before saving. Seeded from
  // the USGA draft when importing; a new id per imported course.
  const seed = useMemo(() => {
    const courseRef = doc(collection(firestore, 'course'));
    if (isImport) {
      if (!draft || !isDraftReady) return null;
      return courseFromUsgaDraft(draft, courseRef.id, creatorId);
    }
    const course: Course = { ...DefaultObjects.Course, itemId: courseRef.id, creatorId };
    return { course, usgaTees: [] };
  // The draft only matters once it's DONE; its contents are then fixed for this id.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImport, usgaCourseId, isDraftReady, creatorId]);

  const handleCreate = useCallback(async (newCourse: Course) => {
    const addedCourse = await addCourse(newCourse);
    if (!addedCourse) {
      console.error('Course not added: ', newCourse);
      return;
    }
    if (isImport && seed && newCourse.usgaCourseId) {
      await createUsgaData(addedCourse.itemId, seed.usgaTees, newCourse.usgaCourseId);
    }
    const remaining = usgaCourseIds.slice(1);
    navigate(remaining.length > 0 ? courseImportPath(remaining) : '/course/' + addedCourse.itemId, { replace: true });
  }, [addCourse, createUsgaData, isImport, navigate, seed, usgaCourseIds]);

  const handleActionsChange = useCallback((actions: ActionProps[] | null, godActionsArg: ReactElement | null) => {
    setPageActions(actions);
    setGodActions(godActionsArg);
  }, []);

  const validateUser = () => {
    return isGod;
  };

  const importHeading = isImport && (
    <Typography variant="bodySmall" color="text.secondary">
      Importing {HANDICAP_LABELS.COURSE_ID} {usgaCourseId}
      {usgaCourseIds.length > 1 ? ` (1 of ${usgaCourseIds.length} to import)` : ''}
    </Typography>
  );

  let content: ReactElement;
  if (seed) {
    content = (
      <CourseEditor
        key={seed.course.itemId}
        course={seed.course}
        isNew
        isImport={isImport}
        handleCreate={handleCreate}
        onActionsChange={handleActionsChange}
      />
    );
  } else if (draft?.status === USGA_COURSE_SEARCH_STATUSES.ERROR) {
    content = (
      <Stack spacing={2}>
        <Alert severity="error">{draft.message ?? 'The USGA import failed.'}</Alert>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" size="small" onClick={() => usgaCourseId && requestUsgaCourseDrafts([usgaCourseId])}>
            Try again
          </Button>
          {usgaCourseIds.length > 1 && (
            <Button variant="text" size="small" onClick={() => navigate(courseImportPath(usgaCourseIds.slice(1)), { replace: true })}>
              Skip this course
            </Button>
          )}
        </Stack>
      </Stack>
    );
  } else {
    content = (
      <Stack spacing={2}>
        <Typography variant="bodySmall" color="text.secondary">Reading the course and its tee sets from the USGA…</Typography>
        <Hourglass variant="inline" />
      </Stack>
    );
  }

  return (
    <GratiPageWrapper
      validateUser={validateUser}
      pageActions={seed ? pageActions : null}
      godActions={seed ? godActions : null}
      isContentLoading={isImport && isDraftLoading}
    >
      <Stack spacing={2}>
        {importHeading}
        {content}
      </Stack>
    </GratiPageWrapper>
  );
}
